import {
  Button,
  formatNumber,
  FullPageLoad,
  generateLinkWithParams,
  getApiPath,
  GridContainer,
  Link,
  linkPlaceholders,
  OverflowContainer,
  routing,
  RowFlex,
  Tab,
  Table,
  Tabs,
  TextField,
  theme,
  Toggle,
  useAxios,
  useLoginContext,
  useModalContext,
  useToast,
} from "common";
import { orderBy } from "lodash";
import { useEffect, useState } from "react";
import { lighten } from "polished";
import { FaArchive, FaBoxOpen, FaEye, FaLink, FaTrash } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const NON_AVAILABILITY_STATUSES = ["Closed", "NoneAvailability", "Denied"];

const linkedViews = [
  { name: "Linked", label: "Linked" },
  { name: "Open", label: "Open" },
  { name: "NonAvailability", label: "Non Availability" },
];

const ConvertConfirm = ({ sr, onCompleted }) => {
  // When a brand request with a similar name already exists the backend
  // responds 409 with the existing brand request so the user can choose to
  // link to it or create a new one instead of just seeing an error.
  const [duplicate, setDuplicate] = useState(null);
  const { alertError } = useToast();
  const { callAxios, loading } = useAxios({
    alertSuccess: "Sourcing Request Converted to Brand Successfully!",
    alertError: false,
    onComplete: onCompleted,
  });

  const convert = (data) =>
    callAxios({
      url: `/sourcingRequests/${sr.id}/convert`,
      method: "POST",
      data,
    }).catch((e) => {
      const existing = e?.response?.data?.existingBrandRequest;
      if (e?.response?.status === 409 && existing) {
        setDuplicate(existing);
      } else {
        alertError(
          e?.response?.data?.error?.message ||
            e?.response?.data?.message ||
            e?.message ||
            "Failed to convert sourcing request.",
        );
      }
    });

  if (duplicate) {
    return (
      <OverflowContainer titleWithBodyAndActions>
        <h2>Duplicate Brand Found</h2>
        <h3>
          A brand request named "{duplicate.brandName}" already exists. Link
          this sourcing request to it, or create a new brand request anyway?
        </h3>
        <Button
          onClick={() =>
            convert({ action: "merge", existingBrandRequestId: duplicate._id })
          }
          disabled={loading}
        >
          {loading ? "Linking..." : "Link to Existing"}
        </Button>
        <Button
          secondary
          onClick={() => convert({ action: "create" })}
          disabled={loading}
        >
          {loading ? "Creating..." : "Create New Anyway"}
        </Button>
      </OverflowContainer>
    );
  }

  return (
    <OverflowContainer titleWithBodyAndActions>
      <h2>Convert Sourcing Request</h2>
      <h3>Are you sure you want to convert {sr.name} to a Brand?</h3>
      <Button onClick={() => convert()} disabled={loading}>
        {loading ? "Converting..." : "Yes, Convert"}
      </Button>
    </OverflowContainer>
  );
};

const DeleteConfirm = ({ sr, onCompleted }) => {
  const { callAxios, loading } = useAxios({
    alertSuccess: "Sourcing Request Deleted Successfully!",
    onComplete: onCompleted,
  });
  return (
    <OverflowContainer titleWithBodyAndActions>
      <h2>Delete Sourcing Request</h2>
      <h3>Are you sure you want to delete {sr.name}?</h3>
      <Button
        onClick={() =>
          callAxios({
            url: `/sourcingRequests/${sr.id}`,
            method: "DELETE",
          })
        }
        disabled={loading}
      >
        {loading ? "Deleting..." : "Yes, Delete"}
      </Button>
    </OverflowContainer>
  );
};

const ArchiveButton = ({ sr, onCompleted }) => {
  const isArchived = !!sr.archived;
  const { callAxios, loading } = useAxios({
    alertSuccess: isArchived
      ? "Sourcing Request Unarchived Successfully!"
      : "Sourcing Request Archived Successfully!",
    onComplete: onCompleted,
  });
  const Icon = isArchived ? FaBoxOpen : FaArchive;
  return (
    <Icon
      title={isArchived ? "Unarchive" : "Archive"}
      style={{ cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.5 : 1 }}
      onClick={() => {
        if (loading) return;
        callAxios({
          url: `/sourcingRequests/${sr.id}`,
          method: "PUT",
          data: { archived: !isArchived },
        });
      }}
    />
  );
};

const DownloadList = () => {
  return (
    <a
      href={getApiPath("sourcingRequestDownload")}
      target="_blank"
      rel="noreferrer"
    >
      <Button>Download List</Button>
    </a>
  );
};

const SourcingRequestList = () => {
  const [search, setSearch] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [linkedView, setLinkedView] = useState("Linked");
  const [sortColumn, setSortColumn] = useState("createdAt");
  const [sortDirection, setSortDirection] = useState("desc");
  const navigate = useNavigate();
  const { setModalContent, closeModal } = useModalContext();
  const { currentUser } = useLoginContext();
  const isSimon = ["simon@hbamfl.com", "hurwitzse@gmail.com"].includes(
    currentUser.email,
  );

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const sortParam = `${sortDirection === "desc" ? "-" : ""}${sortColumn}`;

  const { response: res, refetch } = useAxios({
    callOnLoad: {
      url: "/sourcingRequests",
      params: {
        limit: 100000,
        populate: JSON.stringify(["requestedBy", "brandRequest", "category"]),
        sort: sortParam,
      },
    },
  });

  useEffect(() => {
    refetch();
  }, [sortParam]);

  if (!res) return <FullPageLoad fillWidth />;

  const response = res.data.data
    .filter((r) => (showArchived ? !!r.archived : !r.archived))
    .filter(
      (r) =>
        !search ||
        [r.requestedBy?.name?.toLowerCase(), r.name.toLowerCase()]
          .filter(Boolean)
          .some((v) => v.includes(search.toLowerCase())),
    );

  const [unlinked, allLinked] = ["Open", "Linked"].map((status) =>
    response.filter((d) => d.status === status),
  );

  const isOpenBrandRequest = (sr) =>
    sr.brandRequest?.statuses?.includes("Open");
  const isNonAvailabilityBrandRequest = (sr) =>
    !isOpenBrandRequest(sr) &&
    sr.brandRequest?.statuses?.length > 0 &&
    sr.brandRequest.statuses.every((s) =>
      NON_AVAILABILITY_STATUSES.includes(s),
    );

  const linkedGroups = {
    Open: allLinked.filter(isOpenBrandRequest),
    NonAvailability: allLinked.filter(isNonAvailabilityBrandRequest),
  };
  linkedGroups.Linked = allLinked.filter(
    (sr) =>
      !linkedGroups.Open.includes(sr) &&
      !linkedGroups.NonAvailability.includes(sr),
  );

  const tables = [
    { name: "Open", data: unlinked },
    { name: "Linked", data: linkedGroups[linkedView] },
  ];

  return (
    <OverflowContainer titleWithBody>
      <RowFlex extend>
        <h2>Sourcing Requests</h2>
        <RowFlex>
          <TextField
            value={search}
            label="Search"
            onChange={(e) => setSearch(e)}
          />
          <Toggle
            label="Show Archived"
            fit
            checked={showArchived}
            onChange={(value) => setShowArchived(value)}
          />
          <DownloadList />
          <Button
            onClick={() => {
              navigate(routing.sourcingRequests.add);
            }}
          >
            Add Sourcing Request
          </Button>
        </RowFlex>
      </RowFlex>
      <GridContainer
        noPadding
        columns={2}
        fullWidth
        style={{ height: "100%", overflow: "auto" }}
      >
        {tables.map((table) => {
          const isLinked = table.name === "Linked";
          const style = {
            background: isLinked && lighten(0.2, theme.colors.success),
          };
          return (
            <OverflowContainer key={table.name}>
              {isLinked && (
                <Tabs style={{ height: "auto", marginBottom: "0.5rem" }}>
                  {linkedViews.map(({ name, label }) => (
                    <Tab
                      key={name}
                      isActive={linkedView === name}
                      onClick={() => setLinkedView(name)}
                    >
                      {label}
                      <sup>{linkedGroups[name].length}</sup>
                    </Tab>
                  ))}
                </Tabs>
              )}
              <Table small>
                <thead>
                  <tr>
                    <th onClick={() => handleSort("requestedBy.name")} style={{ cursor: "pointer" }}>
                      Requested By {sortColumn === "requestedBy.name" && (sortDirection === "asc" ? "↑" : "↓")}
                    </th>
                    <th onClick={() => handleSort("createdAt")} style={{ cursor: "pointer" }}>
                      Requested Date {sortColumn === "createdAt" && (sortDirection === "asc" ? "↑" : "↓")}
                    </th>
                    <th onClick={() => handleSort("name")} style={{ cursor: "pointer" }}>
                      Name {sortColumn === "name" && (sortDirection === "asc" ? "↑" : "↓")}
                    </th>
                    <th onClick={() => handleSort("category.name")} style={{ cursor: "pointer" }}>
                      Category {sortColumn === "category.name" && (sortDirection === "asc" ? "↑" : "↓")}
                    </th>
                    <th onClick={() => handleSort("annualProjectionInDollars")} style={{ cursor: "pointer" }}>
                      Annual Projection {sortColumn === "annualProjectionInDollars" && (sortDirection === "asc" ? "↑" : "↓")}
                    </th>
                    <th onClick={() => handleSort("customers.length")} style={{ cursor: "pointer" }}>
                      Customers {sortColumn === "customers.length" && (sortDirection === "asc" ? "↑" : "↓")}
                    </th>
                    <th style={{ width: "1%" }} />
                    {isSimon && <th style={{ width: "1%" }} />}
                    <th style={{ width: "1%" }} />
                    {isSimon && <th style={{ width: "1%" }} />}
                  </tr>
                </thead>
                <tbody>
                  {table.data.map((sr) => {
                    return (
                      <tr key={sr.id}>
                        <td style={style}>{sr.requestedBy?.name}</td>
                        <td style={style}>{new Date(sr.createdAt).toLocaleDateString()}</td>
                        <td style={{ ...style, fontSize: '0.9rem', fontWeight: 'bold' }}>{sr.name}</td>
                        <td style={style}>{sr.category?.name || "-"}</td>
                        <td style={style}>
                          ${formatNumber(sr.annualProjectionInDollars)}
                        </td>
                        <td style={style}>{sr.customers.length || 0}</td>
                        <td style={style}>
                          {sr.status === "Open" && isSimon ? (
                            <FaLink
                              onClick={() => {
                                setModalContent(
                                  <ConvertConfirm
                                    sr={sr}
                                    onCompleted={() => {
                                      closeModal();
                                      refetch();
                                    }}
                                  />,
                                );
                              }}
                            />
                          ) : (
                            sr.brandRequest && (
                              <Link
                                target="_blank"
                                to={generateLinkWithParams(
                                  routing.brandRequests.view,
                                  {
                                    [linkPlaceholders.brandRequestId]:
                                      sr.brandRequest.id,
                                  },
                                )}
                              >
                                {[
                                  ...new Set(
                                    (sr.brandRequest.statuses || []).map((s) =>
                                      s === "FollowUp" ? "WorkedOn" : s,
                                    ),
                                  ),
                                ].join(", ")}
                              </Link>
                            )
                          )}
                        </td>
                        {isSimon && (
                          <td style={style}>
                            {sr.status === "Open" && (
                              <FaTrash
                                color={theme.colors.danger}
                                onClick={() => {
                                  setModalContent(
                                    <DeleteConfirm
                                      sr={sr}
                                      onCompleted={() => {
                                        closeModal();
                                        refetch();
                                      }}
                                    />,
                                  );
                                }}
                              />
                            )}
                          </td>
                        )}
                        <td style={style}>
                          <FaEye
                            onClick={() => {
                              navigate(
                                generateLinkWithParams(
                                  routing.sourcingRequests.view,
                                  {
                                    [linkPlaceholders.sourcingRequestId]: sr.id,
                                  },
                                ),
                              );
                            }}
                          />
                        </td>
                        {isSimon && (
                          <td style={style}>
                            <ArchiveButton
                              sr={sr}
                              onCompleted={() => refetch()}
                            />
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </OverflowContainer>
          );
        })}
      </GridContainer>
    </OverflowContainer>
  );
};

export default SourcingRequestList;
